import { CommonModule } from '@angular/common';
import { Component, DestroyRef, Input, OnChanges, OnInit, SimpleChanges, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';

import { MaterialModule } from 'src/app/core/material.module';
import { SuggestedAddress } from 'src/app/shared/address-autocomplete/address-from-place';
import { AddressSuggestionsComponent } from 'src/app/shared/address-autocomplete/address-suggestions.component';
import { US_STATES } from 'src/app/shared/constants/countries';
import { DirectAddress, DirectAddressCheck } from '../../dormotech-direct.types';
import { DormotechDirectService } from '../../services/dormotech-direct.service';

/** A street line the couriers print on a label: at most 35 characters. */
const LINE_MAX = 35;
const ZIP = /^\d{5}(-\d{4})?$/;

/** The patient fields the address is saved into. */
export interface DirectShippingAddressValue {
  street: string | null;
  street2: string | null;
  city: string | null;
  stateOrProvince: string | null;
  postalCode: string | null;
  countryAlpha2: 'US';
}

/**
 * A Direct patient's shipping address: in the US, and verified with the Dormo
 * Direct lab — it must be able to ship a device there and get it back — before
 * the patient can be created or saved. An address picked from the suggestions
 * is verified on its own; a typed one with the small Verify button at the end.
 * Changing any field after verifying takes the verification back.
 */
@Component({
  selector: 'app-direct-shipping-address',
  templateUrl: './direct-shipping-address.component.html',
  styleUrls: ['./direct-shipping-address.component.scss'],
  imports: [CommonModule, MaterialModule, ReactiveFormsModule, AddressSuggestionsComponent],
})
export class DirectShippingAddressComponent implements OnInit, OnChanges {
  /**
   * The patient's address as saved, on the patient page. The Direct lab took it
   * when it was saved, so it starts out verified; changing it needs verifying.
   */
  @Input() saved: { street?: string | null; street2?: string | null; city?: string | null; stateOrProvince?: string | null; postalCode?: string | null } | null = null;

  private readonly direct = inject(DormotechDirectService);
  private readonly destroyRef = inject(DestroyRef);

  readonly states = US_STATES;
  readonly lineMax = LINE_MAX;

  readonly form = new FormGroup({
    line1: new FormControl<string | null>(null, [Validators.required, Validators.maxLength(LINE_MAX)]),
    line2: new FormControl<string | null>(null, [Validators.maxLength(LINE_MAX)]),
    city: new FormControl<string | null>(null, [Validators.required]),
    state: new FormControl<string | null>(null, [Validators.required]),
    postalCode: new FormControl<string | null>(null, [Validators.required, Validators.pattern(ZIP)]),
  });

  /** The Direct lab's last answer, while the address is still the one it was given. */
  result: DirectAddressCheck | null = null;
  checking = false;
  /** Set when the user tried to move on without validating. */
  needsValidation = false;
  /** The address the answer was for. */
  private checkedKey: string | null = null;
  /** A suggestion was picked but left a field to fill — the state — so verifying waits for it. */
  private verifyWhenComplete = false;

  ngOnInit(): void {
    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (this.checkedKey !== null && this.key() !== this.checkedKey) {
        this.result = null;
        this.checkedKey = null;
      }
      this.needsValidation = false;
      if (this.verifyWhenComplete && this.form.valid && !this.validated) {
        this.verifyWhenComplete = false;
        this.validate();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['saved']) {
      this.load(this.saved);
    }
  }

  /** Shows a saved address as verified: the Direct lab accepted it when it was saved. */
  private load(saved: DirectShippingAddressComponent['saved']): void {
    this.form.reset(
      {
        line1: saved?.street ?? null,
        line2: saved?.street2 ?? null,
        city: saved?.city ?? null,
        state: saved?.stateOrProvince ?? null,
        postalCode: saved?.postalCode ?? null,
      },
      { emitEvent: false },
    );
    this.needsValidation = false;
    this.verifyWhenComplete = false;
    if (this.form.valid) {
      this.result = {
        status: 'DELIVERABLE',
        reason: null,
        message: 'Address validated.',
        problems: [],
        address: { ...this.address(), countryAlpha2: 'US' },
        checkedAt: null,
      };
      this.checkedKey = this.key();
    } else {
      this.result = null;
      this.checkedKey = null;
    }
  }

  /** True once the Direct lab confirmed this exact address. */
  get validated(): boolean {
    return this.result?.status === 'DELIVERABLE' && this.checkedKey === this.key();
  }

  /**
   * Easyship's note when it corrected the address enough that the person should
   * look at the updated fields before saving; null for a plain confirmation.
   */
  get correctionNote(): string | null {
    if (!this.validated || !this.result) return null;
    return this.result.message && this.result.message !== 'Address validated.' ? this.result.message : null;
  }

  /** What went wrong, for anything other than a confirmed address. */
  get refusal(): string | null {
    if (!this.result || this.result.status === 'DELIVERABLE') return null;
    return this.result.message;
  }

  validate(): void {
    this.form.markAllAsTouched();
    if (this.form.invalid || this.checking) {
      return;
    }
    const asked = this.key();
    this.checking = true;
    this.direct.checkAddress(this.address()).subscribe({
      next: (answer) => {
        this.checking = false;
        if (this.key() !== asked) {
          // Edited while the answer was on its way: it is about another address.
          return;
        }
        if (answer.status === 'DELIVERABLE') {
          this.form.patchValue(
            {
              line1: answer.address.line1,
              line2: answer.address.line2,
              city: answer.address.city,
              state: answer.address.state,
              postalCode: answer.address.postalCode,
            },
            { emitEvent: false },
          );
        }
        this.applyProblems(answer);
        this.result = answer;
        this.checkedKey = this.key();
      },
      error: () => {
        this.checking = false;
        this.result = {
          status: 'UNKNOWN',
          reason: null,
          message: "Couldn't check the address right now. Try again.",
          problems: [],
          address: { ...this.address(), countryAlpha2: 'US' },
          checkedAt: null,
        };
        this.checkedKey = this.key();
      },
    });
  }

  /** Back to editing: the address has to be validated again. */
  edit(): void {
    this.result = null;
    this.checkedKey = null;
  }

  /**
   * An address picked from Google's suggestions fills the address; Address Line 2
   * is left as it is. It is not validated by this: like any other edit it takes
   * an earlier validation back, and Validate Address still has to pass.
   */
  applySuggestion(address: SuggestedAddress): void {
    const filled = {
      line1: address.line1,
      city: address.city,
      // Only a state the list has; otherwise it is left for the user to pick.
      state: this.states.some((state) => state.code === address.state) ? address.state : null,
      postalCode: address.postalCode,
    };
    this.form.patchValue(filled);
    for (const name of Object.keys(filled)) {
      this.form.get(name)?.markAsDirty();
    }
    // A picked address is verified straight away; one that left the state to
    // pick is verified as soon as it is picked.
    if (this.form.valid) {
      this.validate();
    } else {
      this.verifyWhenComplete = true;
    }
  }

  /** Called when the user tries to move on: says why they cannot, if they cannot. */
  requireValidation(): boolean {
    if (this.validated) {
      return true;
    }
    this.form.markAllAsTouched();
    this.needsValidation = true;
    return false;
  }

  /** The address in the patient's fields. */
  value(): DirectShippingAddressValue {
    const address = this.address();
    return {
      street: address.line1,
      street2: address.line2,
      city: address.city,
      stateOrProvince: address.state,
      postalCode: address.postalCode,
      countryAlpha2: 'US',
    };
  }

  reset(): void {
    this.form.reset();
    this.edit();
    this.needsValidation = false;
    this.verifyWhenComplete = false;
  }

  hasServerProblem(field: string): boolean {
    return !!this.form.get(field)?.errors?.['server'];
  }

  serverProblem(field: string): string {
    return this.form.get(field)?.errors?.['server'] ?? '';
  }

  private address(): DirectAddress {
    const text = (value: string | null | undefined) => (value && value.trim() ? value.trim() : null);
    const value = this.form.getRawValue();
    return {
      line1: text(value.line1),
      line2: text(value.line2),
      city: text(value.city),
      state: text(value.state),
      postalCode: text(value.postalCode),
    };
  }

  private key(): string {
    return JSON.stringify(this.address());
  }

  /** The Direct lab's objections, on the fields they are about. */
  private applyProblems(answer: DirectAddressCheck): void {
    for (const problem of answer.problems ?? []) {
      const control = this.form.get(problem.field);
      if (control) {
        control.setErrors({ ...(control.errors ?? {}), server: problem.message });
        control.markAsTouched();
      }
    }
  }
}
